import type {
  MarketplaceCategory,
  FulfillmentType,
  ItemTemplate,
} from "./types";

export const CATEGORIES: MarketplaceCategory[] = [
  "GIFT_CARD",
  "EXPERIENCE",
  "PERK",
  "MERCHANDISE",
  "DONATION",
  "OTHER",
];

/** i18n keys — resolve with t() at render time. */
export const CATEGORY_LABEL_KEYS: Record<MarketplaceCategory, string> = {
  GIFT_CARD: "rewards.category.GIFT_CARD",
  EXPERIENCE: "rewards.category.EXPERIENCE",
  PERK: "rewards.category.PERK",
  MERCHANDISE: "rewards.category.MERCHANDISE",
  DONATION: "rewards.category.DONATION",
  OTHER: "rewards.category.OTHER",
};

export const CATEGORY_EMOJI: Record<MarketplaceCategory, string> = {
  GIFT_CARD: "🎁",
  EXPERIENCE: "✨",
  PERK: "☕",
  MERCHANDISE: "👕",
  DONATION: "💝",
  OTHER: "📦",
};

export const FULFILLMENT_LABEL_KEYS: Record<FulfillmentType, string> = {
  MANUAL: "rewards.fulfillment.MANUAL",
  AUTOMATIC: "rewards.fulfillment.AUTOMATIC",
  INSTANT: "rewards.fulfillment.INSTANT",
};

export const FULFILLMENT_HINT_KEYS: Record<MarketplaceCategory, string> = {
  GIFT_CARD: "rewards.fulfillmentHint.GIFT_CARD",
  EXPERIENCE: "rewards.fulfillmentHint.EXPERIENCE",
  PERK: "rewards.fulfillmentHint.PERK",
  MERCHANDISE: "rewards.fulfillmentHint.MERCHANDISE",
  DONATION: "rewards.fulfillmentHint.DONATION",
  OTHER: "rewards.fulfillmentHint.OTHER",
};

/** 1,250 Koins ≈ €10 */
export const KOINS_PER_EUR = 125;

export function koinsToEur(koins: number): string {
  return (koins / KOINS_PER_EUR).toFixed(2);
}

export const ITEM_TEMPLATES: ItemTemplate[] = [
  {
    name: "€10 Amazon Gift Card",
    description:
      "Digital Amazon gift card worth €10. Redeemable on Amazon for millions of products.",
    shortDescription: "€10 digital Amazon voucher",
    koinsPrice: 1250,
    category: "GIFT_CARD",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Purchase a €10 Amazon digital gift card and send the code to the user via fulfillment details.",
    emoji: "🎁",
  },
  {
    name: "€5 Amazon Gift Card",
    description:
      "Digital Amazon gift card worth €5. A great entry-level reward for consistent activity.",
    shortDescription: "€5 digital Amazon voucher",
    koinsPrice: 650,
    category: "GIFT_CARD",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Purchase a €5 Amazon digital gift card and send the code to the user via fulfillment details.",
    emoji: "🎁",
  },
  {
    name: "Coffee Voucher",
    description:
      "Enjoy a complimentary coffee at a local café. Perfect for a mid-day energy boost!",
    shortDescription: "A free coffee at a local café",
    koinsPrice: 325,
    category: "PERK",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Send the user a voucher code or coordinate with the café for validation.",
    emoji: "☕",
  },
  {
    name: "Half-Day PTO",
    description:
      "Take half a day off on the company! Redeemable with manager approval.",
    shortDescription: "Half-day paid time off",
    koinsPrice: 2500,
    category: "EXPERIENCE",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Confirm with the user's manager and HR. Approve the PTO in your HR system.",
    emoji: "🏖️",
  },
  {
    name: "VIP Parking - 1 Week",
    description:
      "Reserved VIP parking spot for one full week. Zero cost to the company!",
    shortDescription: "Premium parking for 1 week",
    koinsPrice: 500,
    category: "PERK",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Assign the VIP parking spot for the agreed week. Notify facilities team.",
    emoji: "🅿️",
  },
  {
    name: "€10 Charity Donation",
    description:
      "Kommit donates €10 to a charity of your choice in your name. Feel great about giving back!",
    shortDescription: "€10 donated to your chosen charity",
    koinsPrice: 1250,
    category: "DONATION",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Ask the user which charity they prefer. Make the donation and share the receipt.",
    emoji: "💝",
  },
  {
    name: "Leave 2h Early Friday",
    description:
      "Leave two hours early any Friday this month. Subject to manager approval.",
    shortDescription: "Early Friday departure",
    koinsPrice: 750,
    category: "EXPERIENCE",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Confirm approval with the user's manager for a specific Friday.",
    emoji: "🎉",
  },
  {
    name: "Company Branded Item",
    description:
      "Choose from company-branded socks, mugs, or notebooks. Show your team spirit!",
    shortDescription: "Branded merch of your choice",
    koinsPrice: 400,
    category: "MERCHANDISE",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions:
      "Ask the user which item they prefer and arrange shipping or desk delivery.",
    emoji: "👕",
  },
];
